import { CompanyAdminLayout } from "../../components/CompanyAdmin/CompanyAdminLayout";
import CircularProgress from "../../components/UI/CircularProgress";
import React, { type MouseEvent, useEffect, useRef, useState } from "react";
import {
  LucideActivity,
  LucideBell,
  LucideClock,
  LucideFileLineChart,
  LucideFilter,
  LucidePlus,
  LucidePrinter,
  LucideTriangle,
  LucideTriangleAlert,
  LucideX,
} from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../../components/UI/Button";
import { useParams } from "react-router-dom";
import type {
  MachineDetailDto,
  RouterMachinesResponse,
} from "../../../domain/types/machine";
import type { RouterInfoResponse } from "../../../domain/types/router";
import { companyMachineUseCase } from "../../../di/container";

const notificationMockJson = `[]`;

const formatRuntime = (runtimeSeconds: number | null): string => {
  if (runtimeSeconds === null || runtimeSeconds === 0) {
    return "0시간 0분";
  }

  const hours = Math.floor(runtimeSeconds / 3600);
  const minutes = Math.floor((runtimeSeconds % 3600) / 60);

  return `${hours}시간 ${minutes}분`;
};

interface MachineNotification {
  id: string;
  machineId: number;
  machineName: string;
  timestamp: string;
  type: "info" | "warning" | "error";
  title: string;
  description: string;
}

const NewEquipmentPage = () => {
  const { companyId } = useParams();

  const [notifications, setNotifications] = useState<
    MachineNotification[] | null
  >(null);

  useEffect(() => {
    const existingLocalStorage = localStorage.getItem("notifications");
    if (existingLocalStorage) {
      setNotifications(JSON.parse(existingLocalStorage));
    } else {
      setNotifications(JSON.parse(notificationMockJson));
    }
  }, []);

  useEffect(() => {
    if (notifications) {
      localStorage.setItem("notifications", JSON.stringify(notifications));
    }
  }, [notifications]);

  const [selectedRouterId, setSelectedRouterId] = useState<"all" | number>(
    "all",
  );
  const [selectedMachineFilter, setSelectedMachineFilter] = useState<
    null | number
  >(null);

  const [machineDetailsByRouter, setMachineDetailsByRouter] =
    useState<RouterMachinesResponse[]>();
  const [routers, setRouters] = useState<RouterInfoResponse[]>([]);
  const [machines, setMachines] = useState<MachineDetailDto[]>([]);

  useEffect(() => {
    setRouters(machineDetailsByRouter?.flatMap((item) => item.router) ?? []);
  }, [machineDetailsByRouter]);
  useEffect(() => {
    setMachines(machineDetailsByRouter?.flatMap((item) => item.machines) ?? []);
  }, [machineDetailsByRouter]);

  useEffect(() => {
    (async () => {
      if (companyId) {
        const fetchedMachineDetailsByRouter =
          await companyMachineUseCase.getMachineDetailsByRouter(
            Number(companyId),
          );
        console.log(fetchedMachineDetailsByRouter);
        setMachineDetailsByRouter(fetchedMachineDetailsByRouter);
      }
    })();
  }, [companyId]);

  const equipmentBlockRef = useRef<HTMLDivElement>(null);
  const [isEquipmentElementRowFlex, setIsEquipmentElementRowFlex] =
    useState(true);

  const [isSidebarMenuOpen, setIsSidebarMenuOpen] = useState(false);

  const handleResize = () => {
    if (!equipmentBlockRef.current) return;

    if (isSidebarMenuOpen) {
      if (equipmentBlockRef.current.clientWidth < 900) {
        setIsEquipmentElementRowFlex(false);
      } else {
        setIsEquipmentElementRowFlex(true);
      }
    } else {
      if (equipmentBlockRef.current.clientWidth < 870) {
        setIsEquipmentElementRowFlex(false);
      } else {
        setIsEquipmentElementRowFlex(true);
      }
    }
  };

  useEffect(() => {
    handleResize();
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    handleResize();
  }, [handleResize, isSidebarMenuOpen]);

  const filterIconClickHandler = (index: number) => {
    setSelectedMachineFilter(index);
    setIsSidebarMenuOpen(true);
  };
  return (
    <>
      <CompanyAdminLayout>
        <div className="p-5 relative">
          <div className="relative mb-7 / flex flex-col items-start gap-[0.5rem]">
            <h2 className="text-[1.375rem] / font-semibold">
              데이터 수집기 관리
            </h2>
            <span className="text-[1rem] font-normal">
              총 {routers.length}개의 데이터 수집기가 있습니다.
            </span>
          </div>
          <div className="grid grid-col-1 xl:grid-cols-2 gap-[20px]">
            {routers.map((item) => {
              const matchingMachines = machineDetailsByRouter?.filter(machine => machine.router.id === item.id)[0].machines;

              return (
                <div className="relative bg-[#F9FAFB] outline outline-[#D1D5DB] outline-1 rounded-[14px] text-start">
                  <img
                    src="/assets/images/router.png"
                    className="absolute -top-[7px] left-[8px] w-[150px] pointer-events-none"
                    alt="라우터 이미지"
                  />

                  <div className="ml-[150px] pt-[40px] mb-[40px]">
                    <h2 className="font-bold text-[20px] mr-[20px]">{item.routerName}</h2>
                    <div>{`PODRUT-${item.id}`}</div>
                  </div>

                  <div className="text-nowrap flex flex-col xl:flex-row gap-[20px] pb-[22px] px-[28px]">
                    <div className="w-full grid grid-cols-2 gap-2">
                      <div className="flex flex-row items-center gap-2">
                        <LucidePrinter />
                        <div className="flex flex-col">
                          <h4 className="text-[12px] text-[#8D8D8D]">
                            총 장비 수
                          </h4>
                          <span className="font-bold">
                            {matchingMachines && matchingMachines.length}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-2">
                        <LucideActivity className="min-w-[19px] w-[19px]" />
                        <div className="flex flex-col">
                          <h4 className="text-[12px] text-[#8D8D8D]">
                            온라인 상태 장비 수
                          </h4>
                          <span className="font-bold">
                            {matchingMachines &&
                              matchingMachines.filter(
                                (item) => item.status === "ONLINE",
                              ).length}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-2">
                        <LucideClock className="min-w-[19px] w-[19px]" />
                        <div className="flex flex-col">
                          <h4 className="text-[12px] text-[#8D8D8D]">
                            대기중인 작업 수
                          </h4>
                          <span className="font-bold">0</span>
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-2">
                        <LucideBell className="min-w-[19px] w-[19px]" />
                        <div className="flex flex-col">
                          <h4 className="text-[12px] text-[#8D8D8D]">
                            알림 수
                          </h4>
                          <span className="font-bold">0</span>
                        </div>
                      </div>
                    </div>
                    <div className="h-[1px] w-full xl:w-[1px] xl:h-auto bg-[#D1D5DB]"></div>
                    <div className="min-w-[150px] grid grid-cols-2 xl:grid-cols-1 gap-2">
                      <div className="flex flex-row items-center gap-2">
                        <LucideTriangleAlert className="min-w-[19px] w-[19px]" />
                        <div className="flex flex-col">
                          <h4 className="text-[12px] text-[#8D8D8D]">
                            오프라인 장비 상태 수
                          </h4>
                          <span className="font-bold">
                            {matchingMachines &&
                              matchingMachines.filter(
                                (item) => item.status === "OFFLINE",
                              ).length}
                          </span>
                        </div>
                      </div>
                      <div className="flex flex-row items-center gap-2">
                        <LucideTriangleAlert className="min-w-[19px] text-[#FF4D4F] w-[19px]" />
                        <div className="flex flex-col">
                          <h4 className="text-[12px] text-[#FF4D4F]">
                            오류 건수
                          </h4>
                          <span className="font-bold text-[#FF4D4F]">0</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </CompanyAdminLayout>
    </>
  );
};

export default NewEquipmentPage;
